/*
  Gift with purchase: corrects a gift line that should not be in the cart as it stands.

  Liquid does the diffing. snippets/cart-drawer.liquid and sections/main-cart-items.liquid
  emit <gwp-guard data-updates='{"<line item key>": <target quantity>}'> ONLY when a gift
  line is wrong (its earning product has gone, there is a second gift, its quantity is
  above 1, or it still costs money because the discount ended). On a healthy cart the
  element is never rendered, so none of this runs.

  Deliberately NOT modelled on cart-bundle-upsell.js:
  - It never calls cartDrawer.renderContents(), which opens the drawer. A returning visitor
    with a stale gift must not have the drawer spring open on page load.
  - Several guards connect for one bad cart: the drawer renders twice per add (once from
    renderContents, once from CartItems.onCartUpdate), and on /cart both the drawer and the
    cart page emit one. Hence the shared lock.
  - The lock is released BEFORE the sections are swapped. Guards in the new markup connect
    synchronously during innerHTML, and a legitimate second pass (quantity 3 -> 1, then
    "still paid" -> 0) would otherwise be blocked for good.
  - Termination does not rest on "every update lowers a quantity": if Shopify refuses a
    write, Liquid would re-emit the guard for ever. Hence the hard cap per page load.
*/
if (!customElements.get('gwp-guard')) {
  let gwpGuardBusy = false;
  let gwpGuardRuns = 0;
  const GWP_GUARD_MAX_RUNS = 3;

  customElements.define(
    'gwp-guard',
    class GwpGuard extends HTMLElement {
      connectedCallback() {
        if (window.Shopify && Shopify.designMode) return;
        if (gwpGuardBusy || gwpGuardRuns >= GWP_GUARD_MAX_RUNS) return;

        let updates;
        try {
          updates = JSON.parse(this.dataset.updates || '{}');
        } catch (e) {
          return;
        }
        if (!Object.keys(updates).length) return;

        gwpGuardBusy = true;
        gwpGuardRuns += 1;
        this.fix(updates);
      }

      async fix(updates) {
        // Both hosts share the { id, section, selector } shape. On /cart that is five unique
        // sections, which is exactly the Section Rendering API limit.
        const hosts = [document.querySelector('cart-drawer-items'), document.querySelector('cart-items')].filter(
          Boolean
        );
        const targets = [];
        hosts.forEach((host) => {
          host.getSectionsToRender().forEach((section) => {
            if (!targets.some((target) => target.section === section.section)) targets.push(section);
          });
        });

        // /cart/change.js is keyed by line index, so a tap mid-flight could hit the wrong line.
        const lists = [document.getElementById('CartDrawer-CartItems'), document.getElementById('main-cart-items')].filter(
          Boolean
        );
        lists.forEach((list) => list.classList.add('cart__items--disabled'));

        let state = null;
        try {
          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              updates,
              sections: targets.map((target) => target.section),
              sections_url: window.location.pathname,
            }),
          });
          state = await response.json();
        } catch (e) {
          console.error('Gift with purchase guard:', e);
        }

        gwpGuardBusy = false;
        lists.forEach((list) => list.classList.remove('cart__items--disabled'));
        if (!state || state.status || !state.sections) return;

        const isEmpty = state.item_count === 0;
        const cartDrawer = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        const cartItems = document.querySelector('cart-items');
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
        if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
        if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);

        targets.forEach((target) => {
          const container = document.getElementById(target.id);
          const html = state.sections[target.section];
          if (!container || !html) return;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
          if (!source) return;
          (container.querySelector(target.selector) || container).innerHTML = source.innerHTML;
        });

        // Only an open drawer has a focus trap to restore; never steal focus for a closed one.
        if (!cartDrawer || !cartDrawer.classList.contains('active')) return;
        if (isEmpty) {
          trapFocus(cartDrawer.querySelector('.drawer__inner-empty'), cartDrawer.querySelector('a'));
        } else if (cartDrawer.querySelector('.cart-item__name')) {
          trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__name'));
        }
      }
    }
  );
}
